import { User, UserPermission } from '@elio/emanager-front/auth/model';
import { Headquarter } from '@elio/emanager-front/offer/model';
import { RouterSelectors } from '@elio/emanager-front/shared/data-access/router';
import { roles } from '@elio/emanager-front/shared/models';
import { PathRouter } from '@elio/emanager-front/shared/tools/router';
import { createCallStateSelector } from '@elio/shared/utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { USER_KEY, UserState } from './user.reducer';

const getUserState = createFeatureSelector<UserState>(USER_KEY);

const getUser = createSelector(getUserState, (userState: UserState) => userState.user);

const getUserToken = createSelector(getUser, (user: User | null) => user?.token);

const getJwtAccessToken = createSelector(getUserState, (userState: UserState) => userState.jwtAccessToken);

const isRememberMe = createSelector(getUserState, (userState: UserState) => userState.remenberMe);

const getUserRoles = createSelector(getUser, (user: User | null) => user?.roles ?? [roles.OPERATION]);

const getUserPermissions = createSelector(getUser, (user: User | null) => user?.permissions ?? []);

const isLoggedIn = createSelector(getUserState, (state: UserState) => state.isLoggedIn);

const { getUserCallState, isUserLoaded, isUserLoading, getUserErrorMessage } = createCallStateSelector(
  USER_KEY,
  getUserState
);

const getUserAccountId = createSelector(getUser, (user: User | null) => user?.accountId);

const isSuperAdmin = createSelector(getUser, (user: User | null) => !!user?.isSuperAdmin);

const getHeadquarters = createSelector(getUser, (user: User | null) => user?.headquarters ?? []);

const getCurrentHeadquarter = createSelector(
  getUser,
  RouterSelectors.getHeadquarterIdFromRouteParam,
  (user: User | null, headquarterId?: number) =>
    user && headquarterId ? user.headquarters.find((h) => h.id === headquarterId) : undefined
);

const getCurrentHeadquarterToken = createSelector(
  getCurrentHeadquarter,
  (headquarter: Headquarter | undefined) => headquarter?.token
);

const getRouteToFirstPage = createSelector(getUser, (user): PathRouter => {
  if (user?.isSuperAdmin) {
    return 'admin';
  } else if (user && user.headquarters.length > 0) {
    return 'headquarters-menu';
  } else if (user && user.offerIds.length > 1 && user.headquarters.length === 0) {
    return 'offer-menu';
  }
  return undefined;
});

/** Returns true if user is super admin or has one of the given permissions */
const hasAnyPermission = (permission: UserPermission | UserPermission[]) =>
  createSelector(getUserPermissions, isSuperAdmin, (userPermissions, isSuperAdmin) => {
    if (isSuperAdmin) return true;

    const permissions: UserPermission[] = Array.isArray(permission) ? permission : [permission];
    return permissions.length === 0 || userPermissions.some((p) => permissions.includes(p.name));
  });

/** Returns true if user is super admin or has all the given permissions */
const hasEveryPermission = (permissions: UserPermission[]) =>
  createSelector(getUserPermissions, isSuperAdmin, (userPermissions, isSuperAdmin) => {
    if (isSuperAdmin) return true;

    const userPermissionsNames = userPermissions.map((p) => p.name);
    return permissions.length === 0 || permissions.every((p) => userPermissionsNames.includes(p));
  });

export const UserSelectors = {
  getUserState,
  getUser,
  getUserToken,
  getJwtAccessToken,
  isRememberMe,
  getUserRoles,
  getUserPermissions,
  isLoggedIn,
  getUserCallState,
  isUserLoaded,
  isUserLoading,
  getUserErrorMessage,
  getUserAccountId,
  isSuperAdmin,
  getHeadquarters,
  getCurrentHeadquarter,
  getCurrentHeadquarterToken,
  hasAnyPermission,
  getRouteToFirstPage,
  hasEveryPermission,
};
